import React, { useEffect, useState } from "react";
import BlockContent from "@sanity/block-content-to-react";
import {
  headerLinkWrapper, richtextBtn,
  sideHeader, richTextImg, table, smallTxt, styledBox, btnContainer,
  videoRenderWrapper, ratingText, clipStar, yStars, fullWidthBox
} from "./style.mod.scss";
import Link from "components/link";
import getImagePath from "helpers/get-image-path";
import { getMergedReviewsForCluster } from "../../data/trusted-reviews";
import { StarFill } from "react-bootstrap-icons";
import locale from "locales";

const Reviews = ({ node }) => {

  const variants = node?.productRef?.variants;

  const [averageRating, setAverageRating] = useState(null);
  const [totalReviews, setTotalReviews] = useState(0);

  useEffect(() => {
    const fetchReviews = async () => {
      const reviews = await getMergedReviewsForCluster(variants);
    
      if (variants && Array.isArray(variants)) {
        const clusters = variants.map(variant => variant.cluster).filter(Boolean);
    
        const filteredReviews = reviews.filter(review => 
          review.cluster && clusters.includes(review.cluster) 
        );
    
        if (filteredReviews.length > 0) {
          const totalScore = filteredReviews.reduce((sum, review) => sum + (review.rating || 0), 0);
          setTotalReviews(filteredReviews.length);
          const avgRating = filteredReviews.length > 0 ? totalScore / filteredReviews.length : 0;
          setAverageRating(avgRating);
        }
      } else {
        console.log("No variants found or variants is not an array.");
      }
    };

    fetchReviews();
  }, [variants]);

  const round = (avg) => avg && Math.round(avg);
  const prec = (avg) => avg && Number.parseFloat(avg).toPrecision(2);


  return (
    <div className={yStars}>
      {
        averageRating &&
        Array(round(averageRating)).fill().map(() => (
          <div key={Math.random()} className={clipStar}><StarFill /></div>
        ))
      }
      {
        averageRating &&
        <span className={ratingText}>{prec(averageRating)} ({totalReviews} {locale.pages.reviews.reviews})</span>
      }
    </div>
  );
};

const LinkHeader = ({ mark, children }) => (
  <span id={mark?.href} className={headerLinkWrapper}>
    {children}
  </span>
);

const VideoRender = ({ node }) => (
  <video className={videoRenderWrapper} type="video/mp4" autoPlay={true} muted={true} playsInline={true} loop>
    <source data-src={node?.url} />
  </video>
);

const smallText = ({ mark, children }) => (
  <small id={mark?.small} className={smallTxt}>
    {children}
  </small>
);

const ImageRenderer = ({ node }) => {
  const alt = node.alt?.replace("Olav ", "Olavson ");
  return (
    <img className={richTextImg} srcSet={getImagePath(node, true)} alt={alt} />
  );
};

const ButtonRenderer = ({ node: { btnLink, btnTitle, leftAlign } }) => (
  <div className={btnContainer} style={leftAlign ? { justifyContent: "flex-start" } : {}}  >
    <Link to={btnLink} className={richtextBtn}>
      {btnTitle}
    </Link>
  </div>
);

const highlight = (props) => <span style={{ backgroundColor: props.mark.color }}>{props.children}</span>;

const ColorRender = (node) => {
  return React.createElement(node._type, { style: { color: node.mark.hex } }, [...node.children]);
};

const embedHTMLSerializer = (props) => <div dangerouslySetInnerHTML={{ __html: props.node.html }} />;

const TableSerializer = ({ node } = node) => {
  let headers = node.rows[0].cells.map((header, i) => { return <th key={i}>{header}</th>; });
  let tableRows = node.rows.slice(1, Infinity).map((tr, i) => { return <tr key={i}> {tr.cells.map((cells, i) => { return <td key={i}>{cells}</td>; })}</tr>; });

  return (
    <div className={table}>
      <table>
        <tr>{headers}</tr>
        {tableRows}
      </table>
    </div>
  );
};

const TableTwoHeadersSerializer = ({ node } = node) => {
  let headers = node.rows[0].cells.map((header, i) => { return <th key={i}>{header}</th>; });
  let tableRows = node.rows.slice(1, Infinity).map((tr, i) => { return <tr key={i}> {tr.cells.map((cells, i) => { return <td className={sideHeader} key={i}>{cells}</td>; })}</tr>; });

  return (
    <div className={table}>
      <table>
        <tr>{headers}</tr>
        {tableRows}
      </table>
    </div>
  );
};

const serializers = {
  types: {
    image: ImageRenderer,
    limitedImage: ImageRenderer,
    button: ButtonRenderer,
    table: TableSerializer,
    tableTwoHeaders: TableTwoHeadersSerializer,
    video: VideoRender,
    product: Reviews,
    embedHTML: embedHTMLSerializer
  },
  marks: {
    sectionHeader: LinkHeader,
    color: ColorRender,
    highlight: highlight,
    small: smallText,
  },
};

const RichText = ({ className, blocks, _serializers, highlightColor, color, fullWidthContainer }) => {
  return (
    <div className={`${highlightColor ? `${className} ${styledBox}` : className} ${fullWidthContainer && fullWidthBox}`}
      style={{
        backgroundColor: highlightColor && highlightColor?.hex, color: color,
      }}>
      <BlockContent
        blocks={blocks}
        serializers={_serializers || serializers}
      />
    </div>
  );
};

export default RichText;
